/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import Head from '@components/atoms/head/head';
import { defaultMetas } from '@constants/metas/default';
import { ALERT_DEFAULT_SORT_VALUE } from '@constants/alerts';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { GetServerSideProps, NextPageWithLayout } from 'next';
import extractFromContext from '@helpers/utils/extract-from-context';
import logger from '@helpers/utils/logger';
import AlertHeader from '@components/alerts/alert-header/alert-header';
import AlertsContent from '@components/alerts/alerts-content/alerts-content';
import { useContextSelector } from 'use-context-selector';
import { useBucket } from '@context/bucket.context';
import { usePreferences } from '@context/preferences.context';
import mapCatalogHitToProduct from '@helpers/utils/catalog/map-catalog-hit-to-product';
import { UserContext } from '@context/user.context';
import { useRouter } from 'next/router';
import { updateParamsInUrl } from '@helpers/routing';
import { parseSearchPreferences, parseURLHashes } from '@helpers/utils/catalog/catalog-url-helpers';
import { scrollToTop } from '@helpers/utils/general';
import SearchService, { ProductSearchUseCase } from '@services/search-service';
import { AlertOrderType } from '@interfaces/models/alert';
import AlertsService, { AlertsQuery } from '@services/alerts-service';
import { useAnalytics } from '@context/analytics/analytics.context';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { ApiResponseMeta } from '@interfaces/api';
import { AlertWithProducts } from '@interfaces/models/alert';
import { useQuery } from '@tanstack/react-query';
import { AlertsCatalogQueryKeys } from '@enums/react-query-keys';
import DefaultLayout from '@layouts/default-layout';
import useUser from '@hooks/user/use-user';
import { getAppInitialProps } from '@helpers/initial-props';
import { getUpdatedUniversalLink } from '@helpers/utils/alerts/universal-link-helper';
import { getSeasUserHeaders } from '@helpers/utils/catalog/catalog-tracking-utils';
import { AxiosHeaders } from 'axios';
import { mapCountryToTheSizeType } from '@helpers/utils/catalog/catalog-url-helpers';
import styles from './index.module.scss';

const defaultLimit = 8;

interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalCount?: number;
}

interface AlarmPageRouterParams {
  pageId?: string;
  limit?: string;
  sortby?: string | string[];
  params?: string[];
}

const fetchData = async (q: AlertsQuery, country, currency, language, regionName, userMySizes, userId) => {
  const { data: alertsData, meta } = await AlertsService.getAlertsNewin(q);

  const processedData = await Promise.all(
    alertsData.map(async (alert) => {
      const { links, criteria, isPaused, ...rest } = alert;

      const searchQuery = criteria?.find((item) => item.key === 'searchQuery')?.value?.[0] ?? '';
      const updatedUniversalLink = getUpdatedUniversalLink(links, criteria);
      const filters = parseURLHashes(updatedUniversalLink)?.filters;
      const isPersonalized = userMySizes && links?.universalLink?.includes('personalized=1');

      const { items } = await SearchService.productSearch(
        {
          sortBy: 'recency',
          pagination: { offset: 0, limit: 5 },
          locale: { country, language, currency, sizeType: mapCountryToTheSizeType(regionName) },
          q: searchQuery,
          filters,
          mySizes: isPersonalized ? { ...userMySizes, isEnabled: true } : null,
        },
        ProductSearchUseCase.savedSearchesLandingPage,
        {
          headers: new AxiosHeaders(getSeasUserHeaders(userId)),
        },
      );

      return {
        ...rest,
        links,
        criteria,
        isPaused,
        items,
      } as AlertWithProducts;
    }),
  );

  return { data: processedData, meta };
};

const getPaginationState = ({ limit, offset, count }: ApiResponseMeta): PaginationState => {
  if (count <= limit) {
    return {
      currentPage: 0,
      totalPages: 0,
      totalCount: count,
    };
  }

  const totalPages = Math.ceil(count / limit);
  const currentPage = offset / limit + 1;
  return {
    totalPages,
    currentPage,
    totalCount: count,
  };
};

const extractErrorFromResponse = (err) => {
  const rsp = err?.response?.data?.errors;
  if (rsp) {
    const { detail, status } = rsp;
    return `${detail} (status: ${status})`;
  }
  return err?.message ?? err.toString();
};

const limitFromRouterParams = (q: AlarmPageRouterParams) => {
  const limit = +q.limit;
  return isNaN(limit) ? defaultLimit : limit;
};

const pageIdFromRouterParams = (q: AlarmPageRouterParams) => {
  if (q.pageId) {
    return +q.pageId || 1;
  }
  if (!q.params?.[1]?.startsWith('p-')) {
    return 1;
  }
  const id = +q.params[1].substring(2);
  return id || 1;
};

const VALID_ALERT_SORT_VALUES: readonly AlertOrderType[] = [
  'newest_item',
  'created_at_desc',
  'created_at_asc',
] as const;

const sortByFromRouterParams = (q: AlarmPageRouterParams): AlertOrderType | undefined => {
  const sortby = Array.isArray(q.sortby) ? q.sortby[0] : q.sortby;
  if (sortby && typeof sortby === 'string' && VALID_ALERT_SORT_VALUES.includes(sortby as AlertOrderType)) {
    return sortby as AlertOrderType;
  }
  return undefined;
};

interface PageQueryParams {
  pageId: number;
  limit: number;
}

interface AlertLandingPageProps {
  isLanding: boolean;
  initialSortBy?: AlertOrderType;
}

const AlertLandingPage: NextPageWithLayout<AlertLandingPageProps> = ({ isLanding, initialSortBy }) => {
  const router = useRouter();
  const { query } = router;
  const { updateDataLayer } = useAnalytics();
  const { sendAnalyticEvent: sendAlertAnalyticEvent } = useAnalyticEvents('alerts');
  const { userId, isAuthenticated } = useUser();
  const { country, currency, language, regionName } = usePreferences();
  const userSizes = useContextSelector(UserContext, (v) => v.userSizes);
  const { isFeatureEnabled } = useBucket();
  const enableSavedSearchesSortingOption = isFeatureEnabled((f) => f.enableSavedSearchesSortingOption, true);

  const [isLoadingState, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queryParams, setQueryParams] = useState<PageQueryParams>();
  const [sortBy, setSortBy] = useState<AlertOrderType | undefined>(initialSortBy);
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
  });
  const [userMySizes, setUserMySizes] = useState(null);
  const pageMetas = defaultMetas[language];

  // Ref to prevent concurrent navigation calls
  const isNavigatingRef = useRef(false);

  // Update sortBy from URL with feature flag logic
  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    if (!enableSavedSearchesSortingOption) {
      setSortBy(undefined);
      return;
    }

    const urlSortBy = sortByFromRouterParams(query);

    if (urlSortBy) {
      setSortBy(urlSortBy);
      return;
    }

    if (initialSortBy) {
      setSortBy(initialSortBy);
      return;
    }

    setSortBy(ALERT_DEFAULT_SORT_VALUE);
  }, [router.isReady, query.sortby, initialSortBy, enableSavedSearchesSortingOption]);

  useEffect(() => {
    if (isAuthenticated) {
      const preferences = parseSearchPreferences(userSizes);
      const mysizes = preferences
        ? {
            ['universe.id']: preferences.universeIds,
            sizes: preferences.sizes,
          }
        : null;
      setUserMySizes(mysizes);
    }
  }, [userSizes, isAuthenticated]);

  useEffect(() => {
    const pageId = pageIdFromRouterParams(query);
    const limit = limitFromRouterParams(query);
    setQueryParams({ pageId, limit });
  }, [query]);

  const alertsQuery = useMemo(() => {
    if (!queryParams) {
      return null;
    }
    const { limit, pageId } = queryParams;

    const baseQuery = {
      limit: limit,
      offset: (pageId - 1) * limit,
    };

    if (enableSavedSearchesSortingOption && sortBy) {
      return {
        ...baseQuery,
        order: sortBy,
      };
    }

    return baseQuery;
  }, [queryParams, enableSavedSearchesSortingOption, sortBy]);

  useEffect(() => {
    updateDataLayer({
      screen_name: '/my_alerts/landing',
      screen_category: 'my_saved_items',
      screen_subcategory: 'my_alerts',
      pageUrl: '/my_alerts/landing',
      env_template: 'my_alerts_landing',
      sorting: sortBy ?? '',
    });
  }, [updateDataLayer, sortBy]);

  const { data, isFetching: isFetchQueryLoading } = useQuery({
    queryKey: [
      AlertsCatalogQueryKeys.FETCH_DATA,
      alertsQuery,
      country,
      currency,
      language,
      regionName,
      userMySizes,
      userId,
    ],
    queryFn: () => fetchData(alertsQuery, country, currency, language, regionName, userMySizes, userId),
    select: ({ data, meta }) => {
      return {
        data: data?.map(({ items, ...rest }) => ({
          ...rest,
          items: items.map((p, i) => ({
            position: i + 1,
            ...mapCatalogHitToProduct(p, country, currency, language, []),
          })),
        })),
        meta,
      };
    },
    onSuccess: ({ meta }) => {
      setError(null);
      const ps = getPaginationState(meta);
      setPagination(ps);
    },
    onError: (err: { status?: number; message?: string }) => {
      logger.error('alerts::AlertsCatalogQuery::', err);
      if (err?.status === 401) {
        return;
      }
      setError(extractErrorFromResponse(err?.message));
    },
    initialData: { data: undefined, meta: {} as ApiResponseMeta },
    enabled: !!alertsQuery,
  });

  const alertsList = data?.data;

  // Shared navigation handler to avoid duplicate router.push logic
  const navigateToAlerts = useCallback(
    ({
      page,
      limit,
      sortBy: newSortBy,
      resetPagination = false,
    }: {
      page?: number;
      limit: number;
      sortBy?: AlertOrderType;
      resetPagination?: boolean;
    }) => {
      // Prevent concurrent navigation calls
      if (isNavigatingRef.current) {
        return;
      }

      isNavigatingRef.current = true;

      const pageId = page && page > 1 ? String(page) : undefined;
      const sortQuery = enableSavedSearchesSortingOption && newSortBy ? `&sortby=${newSortBy}` : '';
      const pageSegment = page && page > 1 ? `/p-${page}` : '';
      const asPath = `/alerts${pageSegment}?limit=${limit}${sortQuery}`;

      router.push(
        {
          pathname: '/alerts/catalog',
          query: {
            ...(pageId ? { pageId } : {}),
            limit,
            ...(enableSavedSearchesSortingOption && newSortBy ? { sortby: newSortBy } : {}),
          },
        },
        asPath,
        { shallow: true },
      );

      if (resetPagination) {
        setPagination({
          currentPage: 1,
          totalPages: 0,
          totalCount: 0,
        });
      }

      scrollToTop();

      // Reset navigation lock after a short delay
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 300);
    },
    [enableSavedSearchesSortingOption, router],
  );

  const handleSortChange = useCallback(
    (newSortBy: AlertOrderType) => {
      const limit = queryParams?.limit ?? defaultLimit;
      const isPageOne = !queryParams?.pageId || queryParams.pageId === 1;
      sendAlertAnalyticEvent('saved_searches_sorting', { label: newSortBy, sorting: newSortBy });

      if (isPageOne) {
        setSortBy(newSortBy);
        updateParamsInUrl({
          limit: String(limit),
          ...(enableSavedSearchesSortingOption && newSortBy ? { sortby: newSortBy } : {}),
        });
        scrollToTop();
      } else {
        navigateToAlerts({ page: 1, limit, sortBy: newSortBy });
      }
    },
    [queryParams, enableSavedSearchesSortingOption, navigateToAlerts, sendAlertAnalyticEvent],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      if (!queryParams) {
        return;
      }

      navigateToAlerts({ page, limit: queryParams.limit, sortBy });
    },
    [queryParams, sortBy, navigateToAlerts],
  );

  const handleLimitChange = useCallback(
    (limit: number) => {
      navigateToAlerts({ page: 1, limit, sortBy, resetPagination: true });
    },
    [sortBy, navigateToAlerts],
  );

  const handleRedirectViewAll = useCallback(
    (alert: AlertWithProducts) => {
      const { id, links, counter, criteria } = alert;
      AlertsService.resetCounter(id);
      sendAlertAnalyticEvent('alert_view_all_items', {
        nb_items: counter?.toString(),
        sorting: sortBy ?? '',
      });
      setLoading(true);

      const updatedUniversalLink = getUpdatedUniversalLink(links, criteria);
      router.push(updatedUniversalLink);
    },
    [sendAlertAnalyticEvent, router, sortBy],
  );

  const isLoading = isFetchQueryLoading || isLoadingState;

  return (
    <>
      <Head metadata={pageMetas} />
      <main className={styles.alertLanding}>
        <AlertHeader
          alertsList={alertsList as unknown as AlertWithProducts[]}
          isLanding={isLanding}
          loading={isLoading}
          isSortEnabled={enableSavedSearchesSortingOption}
          onSortChange={handleSortChange}
          sortBy={sortBy}
          onSortButtonClick={(open) =>
            open && sendAlertAnalyticEvent('saved_searches_sorting', { label: 'open', sorting: sortBy })
          }
        />

        {isAuthenticated && (
          <AlertsContent
            alertsList={alertsList as unknown as AlertWithProducts[]}
            isLoading={isLoading}
            error={error}
            pagination={pagination}
            queryParams={queryParams}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            onRedirectViewAll={handleRedirectViewAll}
            country={country}
            currency={currency}
            language={language}
            sortBy={sortBy}
          />
        )}
      </main>
    </>
  );
};

AlertLandingPage.Layout = [DefaultLayout];
AlertLandingPage.PageType = 'AlertLandingPage';

export const getServerSideProps: GetServerSideProps<AlertLandingPageProps> = async (context) => {
  const { query } = context;
  const { locale } = extractFromContext(context);
  const initialProps = await getAppInitialProps(context);
  const translations = await serverSideTranslations(locale, [
    'default-layout',
    'vc-authentication',
    'a11y',
    'common',
    'saved-searches',
    'alerts',
  ]);

  const sortByFromUrl = sortByFromRouterParams(query);

  return {
    props: {
      ...initialProps,
      ...translations,
      isLanding: true,
      ...(sortByFromUrl ? { initialSortBy: sortByFromUrl } : {}),
    },
  };
};

export default AlertLandingPage;
